// Kreator „Wystaw dokumenty za miesiąc”: sprawdzenie inFaktu, krok na dokument, podsumowanie, wystawienie po kolei.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { docKey, fmtD, money, type Data, type InfaktMonthDoc, type InfaktTemplate } from '@biuro/shared';
import { api } from '../api.ts';
import { monthLower } from '../close.ts';
import { Icon, Sheet } from '../ui.tsx';
import { DONE, EntryDates, EntryLines, evaluateEntry, initialEntry, issuePayload, templateKind, type Entry } from './infaktEntry.tsx';

type Phase = 'checking' | 'steps' | 'summary' | 'issuing' | 'done';
interface Item { id: string; t: InfaktTemplate; doc?: InfaktMonthDoc }
interface Outcome { status: 'issuing' | InfaktMonthDoc['status']; number?: string; message?: string }

/** Kolejność kroków: faktura wewnętrzna, dowód, faktury do kontrahentów. */
const KIND_ORDER: Record<InfaktTemplate['kind'], number> = { internal_invoice: 0, internal_evidence: 1, invoice: 2 };
const ISSUED: Outcome['status'][] = ['created', 'found'];

function stepProblem(t: InfaktTemplate, e: Entry, enabled: boolean): string {
  return evaluateEntry(t, e).inputProblem
    || (t.kind === 'invoice' && !t.clientId ? 'Wybierz w szablonie klienta z inFaktu albo pomiń ten dokument.' : '')
    || (!enabled ? 'inFakt nie jest połączony – brak INFAKT_API_KEY na serwerze.' : '');
}

/** Kwota w podsumowaniu: przychód dowodu albo brutto faktury. */
function amountOf(t: InfaktTemplate, e: Entry) {
  const { sum } = evaluateEntry(t, e);
  return t.kind === 'internal_evidence' ? sum.net : sum.gross;
}

export function MonthWizard({ ym, enabled, onClose }: { ym: string; enabled: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>('checking');
  const [checkNote, setCheckNote] = useState('');
  const [items, setItems] = useState<Item[]>([]);
  const [already, setAlready] = useState<Item[]>([]);
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [skipped, setSkipped] = useState<Record<string, boolean>>({});
  const [step, setStep] = useState(0);
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const started = useRef(false);

  // Najpierw inFakt: dokumenty, które już tam są, nie trafiają do kroków – bez duplikatów.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        await api.infaktCheck(ym);
      } catch (e) {
        setCheckNote(`Nie udało się sprawdzić inFaktu (${(e as Error).message}). Kreator opiera się na tym, co wie aplikacja.`);
      }
      await qc.invalidateQueries({ queryKey: ['data'] });
      const data = qc.getQueryData<Data>(['data']);
      const all = Object.entries(data?.infaktTemplates || {})
        .filter(([, t]) => t.active !== false)
        .map(([id, t]) => ({ id, t, doc: data?.infaktDocs[docKey(id, ym)] }))
        .sort((a, b) => KIND_ORDER[a.t.kind] - KIND_ORDER[b.t.kind] || a.t.name.localeCompare(b.t.name, 'pl'));
      const open = all.filter((x) => !x.doc || !(DONE.includes(x.doc.status) || x.doc.status === 'pending'));
      setItems(open);
      setAlready(all.filter((x) => !open.includes(x)));
      setEntries(Object.fromEntries(open.map((x) => [x.id, initialEntry(x.t, ym, x.doc)])));
      setPhase('steps');
    })();
  }, [qc, ym]);

  const cur = items[step];
  const entry = cur ? entries[cur.id] : undefined;
  const problem = cur && entry ? stepProblem(cur.t, entry, enabled) : '';
  const toIssue = items.filter((x) => !skipped[x.id] && !ISSUED.includes(outcomes[x.id]?.status as Outcome['status']) && outcomes[x.id]?.status !== 'pending');
  const attempted = items.filter((x) => outcomes[x.id]);
  const issuedCount = attempted.filter((x) => ISSUED.includes(outcomes[x.id].status)).length;

  const setEntry = (patch: Partial<Entry>) => setEntries((p) => ({ ...p, [cur.id]: { ...p[cur.id], ...patch } }));
  const go = (i: number) => (i >= items.length ? setPhase('summary') : (setStep(i), setPhase('steps')));
  const next = () => { setSkipped((p) => ({ ...p, [cur.id]: false })); go(step + 1); };
  const skip = () => { setSkipped((p) => ({ ...p, [cur.id]: true })); go(step + 1); };
  const edit = (id: string) => {
    setOutcomes((p) => {
      const rest = { ...p };
      delete rest[id];
      return rest;
    });
    go(items.findIndex((x) => x.id === id));
  };

  // Po kolei, nie równolegle: inFakt ma limity zapytań, a faktury idą przez zlecenia asynchroniczne.
  const issueAll = async () => {
    setPhase('issuing');
    for (const x of toIssue) {
      setOutcomes((p) => ({ ...p, [x.id]: { status: 'issuing' } }));
      try {
        const e = entries[x.id];
        const r = await api.infaktIssue(issuePayload(x.id, x.t, ym, e, evaluateEntry(x.t, e).values));
        setOutcomes((p) => ({ ...p, [x.id]: { status: r.status, number: r.number, message: r.message } }));
      } catch (err) {
        setOutcomes((p) => ({ ...p, [x.id]: { status: 'error', message: (err as Error).message } }));
      }
    }
    await qc.invalidateQueries({ queryKey: ['data'] });
    setPhase('done');
  };

  const title = `Dokumenty za ${monthLower(ym)}`;
  let body: ReactNode;
  let footer: ReactNode = null;

  if (phase === 'checking') {
    body = <p className="muted">Sprawdzam w inFakcie, co jest już wystawione za {monthLower(ym)}…</p>;
  } else if (!items.length) {
    body = <div className="note ok">Wszystkie aktywne dokumenty za {monthLower(ym)} są już w inFakcie.</div>;
    footer = <div className="r"><button className="btn pri" onClick={onClose}>Zamknij</button></div>;
  } else if (phase === 'steps' && cur && entry) {
    body = (
      <>
        {checkNote && <div className="note warn">{checkNote}</div>}
        <ol className="wz-steps" aria-label="Kroki kreatora">
          {items.map((x, i) => (
            <li key={x.id} className={i === step ? 'now' : skipped[x.id] ? 'skip' : i < step ? 'done' : ''} aria-current={i === step ? 'step' : undefined}>
              <span className="dot" aria-hidden="true">{i < step && !skipped[x.id] ? <Icon.check /> : i + 1}</span>
              <span className="nm">{x.t.name}</span>
            </li>
          ))}
          <li className="sum"><span className="dot" aria-hidden="true">{items.length + 1}</span><span className="nm">Podsumowanie</span></li>
        </ol>
        <div className="wz-head">
          <span className="wz-count">Krok {step + 1} z {items.length}</span>
          <h3>{cur.t.name}</h3>
          <div className="kind">{templateKind(cur.t)}</div>
        </div>
        {cur.doc?.status === 'error' && cur.doc.message && <div className="note bad">Poprzednia próba: {cur.doc.message}</div>}
        <EntryLines t={cur.t} entry={entry} onVals={(vals) => setEntry({ vals })} />
        <div className="tcard-act wz-dates"><EntryDates t={cur.t} entry={entry} onChange={setEntry} /></div>
        {problem && <div className="hint act-hint">{problem}</div>}
      </>
    );
    footer = (
      <>
        {step > 0 ? <button className="btn ghost" onClick={() => go(step - 1)}><Icon.prev />Wstecz</button> : <span />}
        <div className="r">
          <button className="btn" onClick={skip}>Pomiń w tym miesiącu</button>
          <button className="btn pri" disabled={!!problem} onClick={next}>Dalej</button>
        </div>
      </>
    );
  } else {
    const showOutcome = phase === 'issuing' || phase === 'done';
    body = (
      <>
        <div className="wz-head">
          <h3>{phase === 'done' ? `Wystawiono ${issuedCount} z ${attempted.length}` : phase === 'issuing' ? 'Wystawiam w inFakcie…' : 'Podsumowanie'}</h3>
          {phase === 'summary' && <div className="kind">Sprawdź kwoty i daty. Dokumenty wystawię po kolei.</div>}
        </div>
        <div className="wz-table-wrap">
          <table className="wz-table" aria-label="Podsumowanie dokumentów">
            <thead><tr><th scope="col">Dokument</th><th scope="col" className="num">Kwota</th><th scope="col">Data</th><th scope="col"><span className="sr-only">Stan</span></th></tr></thead>
            <tbody>
              {items.map((x) => {
                const e = entries[x.id];
                const o = outcomes[x.id];
                return (
                  <tr key={x.id} className={skipped[x.id] ? 'skip' : ''}>
                    <td>
                      <b>{x.t.name}</b><span className="muted small">{templateKind(x.t)}</span>
                      {showOutcome && o?.message && (o.status === 'error' || o.status === 'pending') && <span className={`wz-msg ${o.status}`}>{o.message}</span>}
                    </td>
                    <td className="num">{money(amountOf(x.t, e))}<span className="muted small">{x.t.kind === 'internal_evidence' ? 'przychód' : 'brutto'}</span></td>
                    <td>{fmtD(e.invoiceDate)}{x.t.kind === 'invoice' && <span className="muted small">sprzedaż {fmtD(e.saleDate)}</span>}</td>
                    <td className="wz-state">
                      {skipped[x.id] ? <span className="pill">pominięty</span>
                        : showOutcome && o ? <OutcomeView o={o} />
                        : null}
                      {(phase === 'summary' || (phase === 'done' && (!o || o.status === 'error'))) && (
                        <button className="btn sm ghost" aria-label={`${o?.status === 'error' ? 'Popraw' : 'Zmień'}: ${x.t.name}`} onClick={() => edit(x.id)}>
                          {o?.status === 'error' ? 'Popraw' : 'Zmień'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {already.length > 0 && (
          <p className="muted small wz-already">
            Już w inFakcie: {already.map((x) => `${x.t.name}${x.doc?.number ? ` (${x.doc.number})` : x.doc?.status === 'pending' ? ' (w trakcie)' : ''}`).join(', ')}.
          </p>
        )}
        {phase === 'done' && <div className="note">Następny krok zamknięcia miesiąca: faktury zagraniczne i paczka dla biura.</div>}
      </>
    );
    footer = phase === 'summary' ? (
      <>
        <button className="btn ghost" onClick={() => go(items.length - 1)}><Icon.prev />Wstecz</button>
        <div className="r"><button className="btn pri" disabled={!toIssue.length} onClick={issueAll}>Wystaw wszystkie ({toIssue.length})</button></div>
      </>
    ) : phase === 'done' ? (
      <>
        <a className="btn" href="https://app.infakt.pl" target="_blank" rel="noreferrer"><Icon.ext />Otwórz inFakt</a>
        <div className="r"><button className="btn pri" onClick={onClose}>Zamknij</button></div>
      </>
    ) : null;
  }

  return <Sheet title={title} onClose={onClose} footer={footer} dismissOnBackdrop={false}><div className="wz">{body}</div></Sheet>;
}

/** Krótki stan dokumentu; treść błędu jest pod nazwą dokumentu, żeby nie rozpychała tabeli. */
function OutcomeView({ o }: { o: Outcome }) {
  if (o.status === 'issuing') return <span className="pill warn">wystawiam…</span>;
  if (o.status === 'created' || o.status === 'found') return <span className="pill ok"><span>{o.number || 'wystawiony'}</span></span>;
  if (o.status === 'pending') return <span className="pill warn">w trakcie</span>;
  return <span className="pill bad">błąd</span>;
}
